/**
 * Service Page Layout
 * Shared professional layout system for all service pages.
 */
import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import * as LucideIcons from 'lucide-react';
import { isSafeUrl } from '../../../../utils/isSafeUrl';

const ServicePageLayout = ({ config }) => {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = config.meta?.title || `Uhamiaji SACCOS | ${config.title}`;

    const setMeta = (name, content) => {
      let tag = document.querySelector(name.startsWith('og:')
        ? `meta[property="${name}"]`
        : `meta[name="${name}"]`);
      if (!tag) {
        tag = document.createElement('meta');
        if (name.startsWith('og:')) {
          tag.setAttribute('property', name);
        } else {
          tag.setAttribute('name', name);
        }
        document.head.appendChild(tag);
      }
      tag.setAttribute('content', content);
    };

    const description = config.meta?.description || config.introduction;
    const title = config.meta?.title || `Uhamiaji SACCOS | ${config.title}`;
    setMeta('description', description);
    setMeta('og:title', title);
    setMeta('og:description', description);
  }, [config]);

  const renderSection = (section, index) => {
    switch (section.type) {
      case 'journey':
        return <ProcessSteps key={index} title={section.title} items={section.items} />;
      case 'steps':
        return <StepsSection key={index} title={section.title} items={section.items} />;
      case 'twoColumnInfo':
        return <TwoColumnInfo key={index} left={section.left} right={section.right} />;
      case 'paymentOptions':
        return <PaymentOptions key={index} title={section.title} intro={section.intro} options={section.options} />;
      case 'content':
        return (
          <section key={index} className="py-8 md:py-10 bg-white">
            <div className="container mx-auto px-6 md:px-8 lg:px-12">
              <SectionHeader title={section.title} />
              <div className="max-w-4xl space-y-8">
                {section.blocks.map((block, i) => (
                  <div key={i}>
                    <h3 className="text-lg md:text-xl font-bold text-[#2f3951] mb-3 break-words">
                      {block.heading}
                    </h3>
                    <p className="text-gray-600 leading-relaxed whitespace-pre-line break-words">
                      {block.content}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </section>
        );
      case 'grid':
        return <ServiceCardsGrid key={index} title={section.title} intro={section.intro} items={section.items} />;
      case 'features':
        return <BenefitCards key={index} title={section.title} items={section.items} />;
      case 'highlight':
        return (
          <section key={index} className="py-12 md:py-16 bg-[#2f3951]">
            <div className="container mx-auto px-6 md:px-8 lg:px-12">
              <div className="max-w-4xl mx-auto text-center">
                <div className="w-16 h-16 mx-auto rounded-full bg-[#ca9a37]/20 flex items-center justify-center mb-6">
                  {(() => {
                    const Icon = LucideIcons[section.icon] || LucideIcons.HelpCircle;
                    return <Icon className="w-8 h-8 text-[#ca9a37]" />;
                  })()}
                </div>
                <h2 className="text-2xl md:text-3xl font-bold text-white mb-4 break-words">
                  {section.title}
                </h2>
                <p className="text-gray-300 leading-relaxed whitespace-pre-line break-words">
                  {section.content}
                </p>
              </div>
            </div>
          </section>
        );
      case 'callout':
        return <ServiceCTA key={index} title={section.title} content={section.content} variant={section.variant} primaryAction={section.primaryAction} secondaryAction={section.secondaryAction} />;
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {config.heroCompact ? (
        <CompactHero config={config} />
      ) : (
        <DefaultHero config={config} />
      )}
      {config.sections.map((section, index) => renderSection(section, index))}

      {config.support?.enabled && (
        <section className="py-8 md:py-10 bg-white border-t border-gray-100">
          <div className="container mx-auto px-6 md:px-8 lg:px-12 text-center">
            <h2 className="text-2xl md:text-3xl font-bold text-[#2f3951] mb-4 break-words">
              {config.support.title}
            </h2>
            <p className="text-gray-600 max-w-3xl mx-auto mb-8 break-words whitespace-pre-line">
              {config.support.content}
            </p>
            <ActionButton variant="light" link={config.support.action.link} icon={config.support.action.icon}>
              {config.support.action.label}
            </ActionButton>
          </div>
        </section>
      )}
    </div>
  );
};

/* ─── Sub-Components ─── */

const SectionHeader = ({ title, description }) => (
  <div className="mb-8 md:mb-10">
    <h2 className="text-2xl md:text-3xl font-bold text-[#2f3951] mb-3 break-words hyphens-none">
      {title}
    </h2>
    {description && (
      <p className="text-gray-600 leading-relaxed max-w-4xl break-words whitespace-pre-line">{description}</p>
    )}
  </div>
);

const CompactHero = ({ config }) => {
  const HeroIcon = LucideIcons[config.heroIcon];
  const showActions = config.heroShowActions !== false;
  const showIcon = config.heroShowIcon !== false;

  return (
    <section className="bg-[#2f3951] py-5 md:py-5">
      <div className="container mx-auto px-6 md:px-8 lg:px-12">
        <nav className="flex items-center text-white/80 text-sm mb-3">
          <Link to="/" className="flex items-center hover:text-[#ca9a37] transition-colors">
            <LucideIcons.Home className="w-4 h-4 mr-1" />
            Home
          </Link>
          <LucideIcons.ChevronRight className="w-4 h-4 mx-2" />
          <span className="text-[#ca9a37]">{config.breadcrumb || config.title}</span>
        </nav>

        <h1 className="text-2xl md:text-4xl font-bold text-white mb-2">
          {config.title}
        </h1>
        <div className="w-24 h-1 bg-[#ca9a37] mb-3"></div>

        {config.introduction && (
          <p className="text-base text-gray-200 max-w-2xl line-clamp-3 whitespace-pre-line break-words">
            {config.introduction}
          </p>
        )}

        {showIcon && HeroIcon && (
          <div className="mt-4">
            <HeroIcon className="w-8 h-8 text-[#ca9a37]" />
          </div>
        )}

        {showActions && (config.primaryAction || config.secondaryAction) && (
          <div className="flex flex-col sm:flex-row gap-4 mt-6">
            {config.primaryAction && (
              <ActionButton link={config.primaryAction.link} icon={config.primaryAction.icon}>
                {config.primaryAction.label}
              </ActionButton>
            )}
            {config.secondaryAction && (
              <ActionButton variant="light" link={config.secondaryAction.link} icon={config.secondaryAction.icon}>
                {config.secondaryAction.label}
              </ActionButton>
            )}
          </div>
        )}
      </div>
    </section>
  );
};

const DefaultHero = ({ config }) => {
  const HeroIcon = LucideIcons[config.heroIcon];

  return (
    <section className="bg-[#2f3951] py-12 md:py-20">
      <div className="container mx-auto px-6 md:px-8 lg:px-12">
        <nav className="flex items-center text-white/80 text-sm mb-3">
          <Link to="/" className="flex items-center hover:text-[#ca9a37] transition-colors">
            <LucideIcons.Home className="w-4 h-4 mr-1" />
            Home
          </Link>
          <LucideIcons.ChevronRight className="w-4 h-4 mx-2" />
          <span className="text-[#ca9a37]">{config.breadcrumb || config.title}</span>
        </nav>

        <div className="max-w-3xl">
          {HeroIcon && <HeroIcon className="w-12 h-12 text-[#ca9a37] mb-4" />}
          <h1 className="text-3xl md:text-5xl font-bold text-white mb-4">
            {config.title}
          </h1>
          <p className="text-base md:text-lg text-gray-200 leading-relaxed whitespace-pre-line break-words">
            {config.introduction}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 mt-8">
            {config.primaryAction && (
              <ActionButton link={config.primaryAction.link} icon={config.primaryAction.icon}>
                {config.primaryAction.label}
              </ActionButton>
            )}
            {config.secondaryAction && (
              <ActionButton variant="light" link={config.secondaryAction.link} icon={config.secondaryAction.icon}>
                {config.secondaryAction.label}
              </ActionButton>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

const ActionButton = ({ children, link, icon, variant }) => {
  const Icon = LucideIcons[icon];
  const isExternal = /^https?:\/\//.test(link) || /^mailto:/.test(link);
  const baseClasses =
    variant === 'light'
      ? 'inline-flex items-center justify-center bg-transparent border-2 border-[#2f3951] text-[#2f3951] hover:bg-[#2f3951] hover:text-white font-semibold px-6 py-3 rounded-lg transition-colors'
      : 'inline-flex items-center justify-center bg-[#ca9a37] hover:bg-[#b88a2f] text-white font-semibold px-6 py-3 rounded-lg transition-colors';

  if (isExternal && isSafeUrl(link)) {
    return (
      <a
        href={link}
        target="_blank"
        rel="noopener noreferrer"
        className={baseClasses}
      >
        {Icon && <Icon className="w-4 h-4 mr-2" />}
        {children}
      </a>
    );
  }

  return (
    <Link to={link} className={baseClasses}>
      {Icon && <Icon className="w-4 h-4 mr-2" />}
      {children}
    </Link>
  );
};

const ProcessSteps = ({ title, items }) => (
  <section className="py-8 md:py-10 bg-white">
    <div className="container mx-auto px-6 md:px-8 lg:px-12">
      <SectionHeader title={title} />
      <div
        className={`grid grid-cols-1 sm:grid-cols-2 ${
          items.length > 4 ? 'lg:grid-cols-3 xl:grid-cols-6' : 'lg:grid-cols-4'
        } gap-6`}
      >
        {items.map((step, i) => {
          const StepIcon = LucideIcons[step.icon] || LucideIcons.HelpCircle;
          return (
            <div
              key={i}
              className="group bg-white rounded-xl p-6 border border-gray-100 shadow-sm hover:shadow-lg hover:-translate-y-1 hover:border-[#ca9a37]/30 transition-all"
            >
              <div className="flex items-center justify-between mb-4">
                <span className="text-2xl font-black text-[#ca9a37]">{step.number}</span>
                <div className="w-10 h-10 rounded-full bg-[#ca9a37]/10 flex items-center justify-center group-hover:bg-[#ca9a37] transition-colors">
                  <StepIcon className="w-5 h-5 text-[#ca9a37] group-hover:text-white transition-colors" />
                </div>
              </div>
              <h4 className="font-bold text-[#2f3951] mb-2 break-words text-left">{step.title}</h4>
              <p className="text-gray-600 text-sm leading-relaxed break-words whitespace-pre-line text-left">
                {step.description}
              </p>
            </div>
          );
        })}
      </div>
    </div>
  </section>
);

const StepsSection = ({ title, items }) => (
  <section className="py-8 md:py-10 bg-[#f8f7f7]">
    <div className="container mx-auto px-6 md:px-8 lg:px-12">
      <SectionHeader title={title} />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {items.map((step, i) => {
          const StepIcon = LucideIcons[step.icon] || LucideIcons.HelpCircle;
          return (
            <div
              key={i}
              className="group bg-white rounded-xl p-6 border border-gray-100 shadow-sm hover:shadow-md hover:-translate-y-1 hover:border-[#ca9a37]/30 transition-all"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-[#ca9a37] text-white flex items-center justify-center font-bold flex-shrink-0">
                  {step.number}
                </div>
                {StepIcon && <StepIcon className="w-5 h-5 text-[#ca9a37]" />}
              </div>
              <h3 className="text-lg font-bold text-[#2f3951] mb-3 break-words">{step.heading}</h3>
              <p className="text-gray-600 leading-relaxed break-words whitespace-pre-line text-justify">
                {step.content}
              </p>
            </div>
          );
        })}
      </div>
    </div>
  </section>
);

const TwoColumnInfo = ({ left, right }) => {
  const LeftIcon = LucideIcons[left.icon];
  const RightIcon = LucideIcons[right.icon];
  return (
    <section className="py-12 md:py-16 bg-white">
      <div className="container mx-auto px-6 md:px-8 lg:px-12">
        <div className="grid md:grid-cols-2 gap-8">
          <div className="bg-[#faf6f0] rounded-2xl p-6 md:p-8">
            <div className="flex items-center mb-4">
              {LeftIcon && <LeftIcon className="w-6 h-6 text-[#ca9a37] mr-3" />}
              <h3 className="text-xl font-bold text-[#2f3951] break-words">{left.title}</h3>
            </div>
            <ul className="space-y-3">
              {left.items.map((item, i) => (
                <li key={i} className="flex items-start text-gray-600 text-sm leading-relaxed">
                  <span className="text-[#ca9a37] mr-2 mt-1">&#9656;</span>
                  <span className="break-words">{item}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="bg-[#faf6f0] rounded-2xl p-6 md:p-8">
            <div className="flex items-center mb-4">
              {RightIcon && <RightIcon className="w-6 h-6 text-[#ca9a37] mr-3" />}
              <h3 className="text-xl font-bold text-[#2f3951] break-words">{right.title}</h3>
            </div>
            <ul className="space-y-4">
              {right.items.map((item, i) => (
                <li key={i}>
                  <span className="font-semibold text-[#2f3951] text-sm break-words">{item.label}:</span>
                  <p className="text-gray-600 text-sm leading-relaxed break-words">{item.text}</p>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
};

const PaymentOptions = ({ title, intro, options }) => (
  <section className="py-12 md:py-16 bg-white">
    <div className="container mx-auto px-6 md:px-8 lg:px-12">
      <SectionHeader title={title} description={intro} />
      <div className="grid md:grid-cols-2 gap-6">
        {options.map((option, i) => {
          const OptionIcon = LucideIcons[option.icon] || LucideIcons.HelpCircle;
          return (
            <div key={i} className="bg-[#f4f4f4] rounded-xl p-6 md:p-8">
              <div className="flex items-center mb-3">
                <div className="w-10 h-10 rounded-full bg-[#ca9a37]/10 flex items-center justify-center mr-3">
                  <OptionIcon className="w-5 h-5 text-[#ca9a37]" />
                </div>
                <h3 className="text-lg font-bold text-[#2f3951] break-words">{option.heading}</h3>
              </div>
              <p className="text-gray-600 leading-relaxed break-words whitespace-pre-line">
                {option.content}
              </p>
            </div>
          );
        })}
      </div>
    </div>
  </section>
);

const ServiceCardsGrid = ({ title, intro, items }) => (
  <section className="py-12 md:py-16 bg-gray-50">
    <div className="container mx-auto px-6 md:px-8 lg:px-12">
      <SectionHeader title={title} description={intro} />
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-6">
        {items.map((item, i) => {
          const ItemIcon = LucideIcons[item.icon] || LucideIcons.HelpCircle;
          return (
            <div
              key={i}
              className="bg-white rounded-xl p-5 border border-gray-100 shadow-sm hover:shadow-md hover:-translate-y-1 transition-all"
            >
              <div className="w-11 h-11 rounded-lg bg-[#2f3951]/10 flex items-center justify-center mb-4">
                <ItemIcon className="w-6 h-6 text-[#2f3951]" />
              </div>
              <h4 className="font-semibold text-[#2f3951] text-sm break-words">{item.title}</h4>
            </div>
          );
        })}
      </div>
    </div>
  </section>
);

const BenefitCards = ({ title, items }) => (
  <section className="py-12 md:py-16 bg-white">
    <div className="container mx-auto px-6 md:px-8 lg:px-12">
      <SectionHeader title={title} />
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {items.map((item, i) => {
          const ItemIcon = LucideIcons[item.icon] || LucideIcons.HelpCircle;
          return (
            <div key={i} className="bg-white rounded-xl p-6 border border-gray-100 shadow-sm">
              <div className="w-11 h-11 rounded-lg bg-[#ca9a37]/10 flex items-center justify-center mb-4">
                <ItemIcon className="w-6 h-6 text-[#ca9a37]" />
              </div>
              <h4 className="font-semibold text-[#2f3951] mb-2 break-words">{item.title}</h4>
              <p className="text-gray-600 text-sm leading-relaxed break-words whitespace-pre-line">{item.description}</p>
            </div>
          );
        })}
      </div>
    </div>
  </section>
);

const ServiceCTA = ({ title, content, primaryAction, secondaryAction, variant }) => {
  const isLight = variant === 'light';
  return (
    <section className={`py-8 md:py-10 ${isLight ? 'bg-[#f8f7f7]' : 'bg-[#2f3951]'}`}>
      <div className="container mx-auto px-6 md:px-8 lg:px-12 text-center">
        <h2 className={`text-2xl md:text-3xl font-bold mb-4 break-words ${isLight ? 'text-[#2f3951]' : 'text-white'}`}>
          {title}
        </h2>
        <p className={`max-w-3xl mx-auto mb-8 break-words whitespace-pre-line ${isLight ? 'text-gray-600' : 'text-gray-200'}`}>
          {content}
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          {primaryAction && (
            <ActionButton link={primaryAction.link} icon={primaryAction.icon}>
              {primaryAction.label}
            </ActionButton>
          )}
          {secondaryAction && (
            <ActionButton variant="light" link={secondaryAction.link} icon={secondaryAction.icon}>
              {secondaryAction.label}
            </ActionButton>
          )}
        </div>
      </div>
    </section>
  );
};

export default ServicePageLayout;
